import React, { useState, useRef, useEffect, useMemo } from "react";
import TimeTable from "../TimeTable"; // reuse your existing timetable
import { Card } from "flowbite-react";
import { ScorePopover } from "../BuildSchedule/ScorePopover";
import { doc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase";

// Same color interpolation logic from SchedulePage
const hexToRgb = (hex) => {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    hex = hex.split("").map((c) => c + c).join("");
  }
  const intVal = parseInt(hex, 16);
  return [
    (intVal >> 16) & 255,
    (intVal >> 8) & 255,
    intVal & 255,
  ];
};

const rgbToHex = (r, g, b) => {
  const toHex = (c) => {
    const hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  };
  return "#" + toHex(r) + toHex(g) + toHex(b);
};

const interpolateColor = (color1, color2, factor) => {
  const rgb1 = hexToRgb(color1);
  const rgb2 = hexToRgb(color2);
  const result = rgb1.map((c, i) =>
    Math.round(c + factor * (rgb2[i] - c))
  );
  return rgbToHex(...result);
};

function getColorsForScore(score) {
  const redBg = "#fee2e2";
  const yellowBg = "#fef9c3";
  const greenBg = "#d1fae5";
  const redText = "#b91c1c";
  const yellowText = "#92400e";
  const greenText = "#047857";

  let bgColor, textColor;

  if (score <= 50) {
    const factor = score / 50;
    bgColor = interpolateColor(redBg, yellowBg, factor);
    textColor = interpolateColor(redText, yellowText, factor);
  } else {
    const factor = (score - 50) / 50;
    bgColor = interpolateColor(yellowBg, greenBg, factor);
    textColor = interpolateColor(yellowText, greenText, factor);
  }

  return { bgColor, textColor };
}

const colours = [
  "#FFCDD2", "#C8E6C9", "#FFE0B2", "#BBDEFB",
  "#FFF9C4", "#E1BEE7", "#F8BBD0"
];

function ShoppingCart({
  shoppingCart,
  formatTime,
  parseTime,
  onAddCourses,
  currentIndex,
  setCurrentIndex,
  currentUser,
  onCourseClick
}) {
  const [showScorePopover, setShowScorePopover] = useState(false);
  const scoreButtonRef = useRef(null);

  const removeCurrentSchedule = async () => {
    const scheduleToRemove = shoppingCart[currentIndex];
    if (!scheduleToRemove || !currentUser) return;
    try {
      await deleteDoc(doc(db, "users", currentUser.uid, "scheduleCart", scheduleToRemove.id));
    } catch (error) {
      console.error("Error removing schedule from cart", error);
    }
  };

  useEffect(() => {
    if (shoppingCart.length > 0 && currentIndex >= shoppingCart.length) {
      setCurrentIndex(0);
    }
  }, [shoppingCart, currentIndex, setCurrentIndex]);

  const activeSchedule = shoppingCart[currentIndex] || null;
  const score = typeof activeSchedule?.score === "number" ? activeSchedule.score : null;

  const showPrev = () => {
    if (shoppingCart.length === 0) return;
    setCurrentIndex((prev) => (prev === 0 ? shoppingCart.length - 1 : prev - 1));
  };
  const showNext = () => {
    if (shoppingCart.length === 0) return;
    setCurrentIndex((prev) => (prev === shoppingCart.length - 1 ? 0 : prev + 1));
  };

  // Attempt to read instructor info from various places
  const getInstructorInfo = (course) => {
    // We try multiple paths as you’ve done previously
    if (!course) return "TBA";

    // Possibly check direct enrollment data
    if (course.sfuData?.enrollment) {
      const inst = course.sfuData.enrollment.instructor || course.sfuData.enrollment.instructorName;
      if (inst) return inst;
    }
    // Then check sfuData.instructor / sfuData.instructors / sfuData.info?.instructors
    let instructorData = course.sfuData?.instructor ||
      course.sfuData?.instructors ||
      course.sfuData?.info?.instructors ||
      null;
    if (Array.isArray(instructorData)) {
      const names = instructorData
        .map(inst => inst.name || inst.commonName)
        .filter(Boolean)
        .join(", ");
      return names || "TBA";
    } else if (instructorData && typeof instructorData === "object") {
      return instructorData.name || instructorData.commonName || "TBA";
    }
    // Or check availableSections
    if (Array.isArray(course.availableSections)) {
      for (const sec of course.availableSections) {
        if (sec.sfuData?.instructor || sec.sfuData?.instructors || sec.sfuData?.info?.instructors) {
          // same logic recursively
          return getInstructorInfo(sec);
        }
      }
    }

    return "TBA";
  };

  const getCombinedInstructorInfo = (courses) => {
    const names = courses.map(getInstructorInfo).filter(n => n && n !== "TBA");
    const unique = [...new Set(names)];
    return unique.join(", ") || "TBA";
  };

  const getUniqueJoined = (courses, accessor, fallback = "-") => {
    const values = courses.map(accessor).filter(Boolean);
    const unique = [...new Set(values)];
    return unique.join(", ") || fallback;
  };

  // Group courses by dept_number
  const groupedCourses = useMemo(() => {
    if (activeSchedule && Array.isArray(activeSchedule.combination)) {
      return activeSchedule.combination.reduce((acc, course) => {
        const key = `${course.dept}_${course.number}`;
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(course);
        return acc;
      }, {});
    }
    return {};
  }, [activeSchedule]);

  // Create random class # and seats open to fill table columns
  const [randomMapping, setRandomMapping] = useState({});
  useEffect(() => {
    // Re-generate whenever we have a new schedule
    const newMapping = {};
    for (const groupKey of Object.keys(groupedCourses)) {
      const randomClassNumber = Math.floor(Math.random() * 10000); // 0-9999
      const maxSeats = Math.floor(Math.random() * 31) + 20; // 20 to 50
      const currentSeats = Math.floor(Math.random() * (maxSeats + 1));
      newMapping[groupKey] = {
        randomClassNumber,
        randomSeatsOpen: `${currentSeats}/${maxSeats}`
      };
    }
    setRandomMapping(newMapping);
  }, [groupedCourses]);

  return (
    <div className="w-full"> {/* Ensure full width */}
      {shoppingCart.length === 0 ? (
        <div
          id="alert-empty-cart"
          className="p-4 border border-gray-300 rounded-lg bg-gray-50 dark:border-gray-600 dark:bg-gray-800"
          role="alert"
        >
          <div className="flex items-center">
            <svg
              className="shrink-0 w-4 h-4 me-2 dark:text-gray-300"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M10 .5a9.5 9.5 0 1 0 9.5 
                       9.5A9.51 9.51 0 0 0 10 
                       .5ZM9.5 4a1.5 1.5 0 1 1 0 
                       3 1.5 1.5 0 0 1 0-3ZM12 
                       15H8a1 1 0 0 1 0-2h1v-3H8a1 
                       1 0 0 1 0-2h2a1 1 0 0 1 1 
                       1v4h1a1 1 0 0 1 0 2Z" />
            </svg>
            <span className="sr-only">Info</span>
            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-300">
              Your Cart is Empty
            </h3>
          </div>
          <div className="mt-2 mb-4 text-sm text-gray-800 dark:text-gray-300">
            You haven't added any schedule to your cart yet.
          </div>
          <div className="flex">
            <button
              type="button"
              onClick={onAddCourses}
              className="text-white bg-gray-700 hover:bg-gray-800 
                         focus:ring-4 focus:outline-none focus:ring-gray-300 
                         font-medium rounded-lg text-xs px-3 py-1.5 inline-flex items-center 
                         dark:bg-gray-600 dark:hover:bg-gray-500 dark:focus:ring-gray-800"
            >
              <svg
                className="me-2 h-3 w-3 dark:text-gray-300"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 20 14"
              >
                <path d="M10 0C4.612 0 0 5.336 0 
                         7c0 1.742 3.546 7 10 
                         7 6.454 0 10-5.258 
                         10-7 0-1.664-4.612-7-10-7Zm0 
                         10a3 3 0 1 1 0-6 3 3 0 0 
                         1 0 6Z" />
              </svg>
              Build Schedule
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex justify-end mb-1 space-x-1">
            {/* Example top‐bar action buttons (could remove or customize) */}
            <button
              type="button"
              className="text-blue-700 hover:text-white border border-blue-700 hover:bg-blue-800 
                         focus:ring-4 focus:outline-none focus:ring-blue-300 
                         font-medium rounded-lg text-sm px-3 py-2 me-2 mb-2 
                         dark:border-blue-500 dark:text-blue-500 dark:hover:text-white 
                         dark:hover:bg-blue-500 dark:focus:ring-blue-800 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24"
                strokeWidth={1.5} stroke="currentColor"
                className="w-4 h-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M21.75 8.25l-9.629 
                     6.419c-.682.454-1.56.454-2.243 
                     0L2.25 8.25" />
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M2.25 19.5h19.5V4.5H2.25v15z" />
              </svg>
              Email
            </button>
            <button
              type="button"
              className="text-gray-900 hover:text-white border border-gray-800 hover:bg-gray-900 
                         focus:ring-4 focus:outline-none focus:ring-gray-300 
                         font-medium rounded-lg text-sm px-3 py-2 me-2 mb-2 
                         dark:border-gray-600 dark:text-gray-400 dark:hover:text-white 
                         dark:hover:bg-gray-600 dark:focus:ring-gray-800 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24"
                strokeWidth={1.5} stroke="currentColor"
                className="mt-1 w-4 h-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M9 12.75l2.25 2.25L15 
                     11.25" />
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M12 2.25c2.344 
                     0 4.594.54 6.356 1.514 1.762.972 
                     2.894 2.317 2.894 3.736 0 1.419-1.132 
                     2.764-2.894 3.736-1.762.975-4.012 
                     1.515-6.356 1.515s-4.594-.54-6.356-1.515C3.882 
                     10.264 2.75 8.919 2.75 7.5c0-1.419 
                     1.132-2.764 2.894-3.736C7.406 
                     2.79 9.656 2.25 12 2.25z" />
              </svg>
              Validate
            </button>
            <button
              type="button"
              className="text-green-700 hover:text-white border border-green-700 hover:bg-green-800 
                         focus:ring-4 focus:outline-none focus:ring-green-300 
                         font-medium rounded-lg text-sm px-3 py-2 me-2 mb-2 
                         dark:border-green-500 dark:text-green-500 dark:hover:text-white 
                         dark:hover:bg-green-600 dark:focus:ring-green-800 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24"
                strokeWidth={1.5} stroke="currentColor"
                className="w-4 h-4"
              >
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M16.862 2.487c.32-.32.787-.469 
                     1.255-.403l1.605.22c.929.127 
                     1.673.87 1.8 1.8l.22 1.605c.065.468-.083.936-.403 
                     1.255l-9.68 9.68a1.875 1.875 0 
                     01-.842.48l-4.867 1.218a.75.75 
                     0 01-.91-.91l1.218-4.867a1.875 
                     1.875 0 01.48-.842l9.68-9.68z" />
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M19.5 11.25V19.5a2.25 
                     2.25 0 01-2.25 2.25h-12A2.25 2.25 
                     0 013 19.5v-12A2.25 2.25 
                     0 015.25 5.25h8.25" />
              </svg>
              Edit Cart
            </button>
            <button
              type="button"
              className="text-red-700 hover:text-white border border-red-700 hover:bg-red-800 
                         focus:ring-4 focus:outline-none focus:ring-red-300 
                         font-medium rounded-lg text-sm px-3 py-2 me-2 mb-2 
                         dark:border-red-500 dark:text-red-500 dark:hover:text-white 
                         dark:hover:bg-red-600 dark:focus:ring-red-900 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24"
                strokeWidth={1.5} stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6.75 15.75v3A2.25 2.25 0 
                     009 21h6a2.25 2.25 0 
                     002.25-2.25v-3m0-6.75h3a.75.75 
                     0 01.75.75v6a2.25 2.25 
                     0 01-2.25 2.25H18m-12 
                     0h-.75A2.25 2.25 0 
                     013 15.75v-6a.75.75 0 
                     01.75-.75h3m0 0V4.5A1.5 
                     1.5 0 018.25 3h7.5A1.5 
                     1.5 0 0117.25 4.5v3.75m-9 
                     0h9"
                />
              </svg>
              Print
            </button>
            <button
              type="button"
              className="text-purple-700 hover:text-white border border-purple-700 hover:bg-purple-800 
                         focus:ring-4 focus:outline-none focus:ring-purple-300 
                         font-medium rounded-lg text-sm px-3 py-2 me-2 mb-2 
                         dark:border-purple-400 dark:text-purple-400 dark:hover:text-white 
                         dark:hover:bg-purple-500 dark:focus:ring-purple-900 flex items-center gap-1"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none" viewBox="0 0 24 24"
                strokeWidth={1.5} stroke="currentColor"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.75 7.5c0 
                     2.071-1.679 3.75-3.75 
                     3.75s-3.75-1.679-3.75-3.75 
                     1.679-3.75 3.75-3.75 
                     3.75 1.679 3.75 3.75zM19.5 
                     11.25v6.75a.75.75 
                     0 01-.75.75H5.25a.75.75 
                     0 01-.75-.75v-6.75m14.25 
                     0a3 3 0 00-3-3H8.25a3 
                     3 0 00-3 3m14.25 
                     0H21m-2.25 3h3m-1.5 
                     -1.5v3"
                />
              </svg>
              Register
            </button>
          </div>

          {activeSchedule && (
            <Card>
              <div className="grid grid-cols-3 items-center">
                <div className="inline-block relative">
                  {score !== null && (
                    <button
                      ref={scoreButtonRef}
                      onMouseEnter={() => setShowScorePopover(true)}
                      onMouseLeave={() => setShowScorePopover(false)}
                      data-popover-target="popover-no-arrow"
                      style={{
                        ...getColorsForScore(score),
                        backgroundColor: getColorsForScore(score).bgColor,
                        color: getColorsForScore(score).textColor
                      }}
                      className="text-lg font-semibold rounded-lg px-2 py-1 mr-2 flex items-center 
                                 focus:outline-none focus:ring-2 focus:ring-offset-2"
                    >
                      Score: {score}/100
                    </button>
                  )}
                  <ScorePopover
                    anchorRef={scoreButtonRef}
                    visible={showScorePopover}
                    combination={activeSchedule.combination}
                    preferences={{}} // if you have preference data, pass here
                    parseTime={parseTime}
                  />
                </div>
                <div className="flex items-center justify-center space-x-2">
                  <button
                    onClick={showPrev}
                    type="button"
                    className="p-2 text-gray-900 focus:outline-none bg-white rounded-full border 
                               border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 
                               focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 
                               dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 
                               dark:hover:text-white dark:hover:bg-gray-700"
                    aria-label="Previous schedule"
                  >
                    <svg
                      aria-hidden="true"
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <span className="text-sm text-gray-600">
                    Schedule {currentIndex + 1} of {shoppingCart.length}
                  </span>
                  <button
                    onClick={showNext}
                    type="button"
                    className="p-2 text-gray-900 focus:outline-none bg-white rounded-full border 
                               border-gray-200 hover:bg-gray-100 hover:text-blue-700 
                               focus:z-10 focus:ring-4 focus:ring-gray-100 
                               dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 
                               dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
                    aria-label="Next schedule"
                  >
                    <svg
                      aria-hidden="true"
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <div className="flex items-center justify-end space-x-2">
                  <button
                    onClick={removeCurrentSchedule}
                    type="button"
                    className="focus:outline-none text-white bg-red-700 hover:bg-red-800 
                               focus:ring-4 focus:ring-red-300 font-medium rounded-lg 
                               text-sm px-3 py-2.5"
                  >
                    Remove from Cart
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto mb-4 mt-4">
                <table className="min-w-full border text-sm">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="px-3 py-2 border">Class #</th>
                      <th className="px-3 py-2 border">Section</th>
                      <th className="px-3 py-2 border">Subject</th>
                      <th className="px-3 py-2 border">Course</th>
                      <th className="px-3 py-2 border">Seats Open</th>
                      <th className="px-3 py-2 border">Instructor</th>
                      <th className="px-3 py-2 border">Day(s) &amp; Location(s)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(groupedCourses).map(([groupKey, courses], index) => {
                      const { randomClassNumber, randomSeatsOpen } = randomMapping[groupKey] || {};
                      const sections = getUniqueJoined(courses, c => c.section || c.sfuData?.section);
                      const subject = courses[0].dept.toUpperCase();
                      const courseNum = courses[0].number;
                      const instructor = getCombinedInstructorInfo(courses);

                      // Combine day(s) & location(s)
                      const dayLocations = courses.flatMap(c => {
                        if (c.sfuData?.courseSchedule?.length) {
                          return c.sfuData.courseSchedule.map(mtg => {
                            const d = mtg.days || "";
                            const st = mtg.startTime || "";
                            const et = mtg.endTime || "";
                            return `${d} ${st}–${et} (${mtg.location || "TBD"})`;
                          });
                        }
                        return [];
                      });
                      const dayLocation = [...new Set(dayLocations)].join("; ") || "-";

                      return (
                        <tr key={groupKey + "-" + index} className="border-b">
                          <td className="px-3 py-2 border">{randomClassNumber}</td>
                          <td className="px-3 py-2 border">{sections}</td>
                          <td className="px-3 py-2 border">{subject}</td>
                          <td className="px-3 py-2 border">{courseNum}</td>
                          <td className="px-3 py-2 border">{randomSeatsOpen}</td>
                          <td className="px-3 py-2 border">{instructor}</td>
                          <td className="px-3 py-2 border">{dayLocation}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {(() => {
                if (!activeSchedule?.combination) return null;
                // Optionally color each course block
                const coloredCombination = activeSchedule.combination.map((course, i) => ({
                  ...course,
                  customColor: course.customColor || colours[i % colours.length],
                }));

                return (
                  <TimeTable
                    currentCombination={coloredCombination}
                    breaks={activeSchedule.breaks || []}
                    parseTime={parseTime}
                    formatHour={(hour) => {
                      const period = hour < 12 ? "AM" : "PM";
                      const adjusted = hour % 12 === 0 ? 12 : hour % 12;
                      return `${adjusted}:00 ${period}`;
                    }}
                    formatTime={formatTime}
                    plannedCourses={coloredCombination}
                    onCourseClick={onCourseClick} // IMPORTANT: same onCourseClick
                  />
                );
              })()}
            </Card>
          )}
        </>
      )}
    </div>
  );
}

export default ShoppingCart;
